import React, { ReactNode } from 'react';
import { commonUtils, Link as SatoLink, Skeleton } from '@sato/components';
import { observer } from 'mobx-react-lite';

import { Link } from '@components/common/Link';
import { useApartmentProperties } from '@hooks/useApartmentProperties';
import { useOmaSATOSaunas } from '@hooks/useOmaSATOSaunas';
import { useParkingProducts } from '@hooks/useParkingProducts';
import { ServiceParty } from '@models/realEstate.types';
import { apartmentStore } from '@stores/apartmentStore';
import { localizationStore } from '@stores/localization';

import { IInlineDynamicTextFields } from '../types';

type PropertyType = IInlineDynamicTextFields['property'];

const propertyToServicePartyPropertyMap = {
  name: 'name',
  address: 'addressComposite',
  email: 'email',
  website: 'webSiteUrl',
  city: 'city',
  openingHours: 'serviceOpenHours',
  phoneHotline: 'phoneHotline',
  phoneNumber: 'phone',
  postalCode: 'postalCode',
} satisfies Record<NonNullable<PropertyType>, keyof ServiceParty>;

const linkProperties = [
  'email',
  'website',
  'phoneNumber',
  'phoneHotline',
] satisfies Array<PropertyType>;

type LinkProperty = (typeof linkProperties)[number];

const linkTypeToHrefPrefix = {
  email: 'mailto:',
  phoneNumber: 'tel:',
  phoneHotline: 'tel:',
  website: undefined,
} satisfies Record<LinkProperty, string | undefined>;

const isLinkProperty = (
  property: PropertyType | undefined
): property is LinkProperty =>
  !!property &&
  linkProperties.some((linkProperty) => linkProperty === property);

export const InlineTextWrapper = ({
  children,
  property,
  href = typeof children === 'string' ? children : undefined,
}: {
  children?: ReactNode;
  property?: PropertyType;
  href?: string;
}) => {
  // render nothing if children data is nullable
  if (commonUtils.isNullable(children)) return null;

  if (isLinkProperty(property)) {
    return (
      <SatoLink
        wrapperComponent={Link}
        href={`${linkTypeToHrefPrefix[property] ?? ''}${href}`}
        className="text-brand-turquoise-dark"
      >
        {children}
      </SatoLink>
    );
  }

  return <span className="text-inherit">{children}</span>;
};

const DynamicServicePartyProperty = ({
  party,
  property,
  fallbackValue = 'name',
  alias,
}: {
  party: ServiceParty | undefined;
  property: PropertyType;
  fallbackValue?: PropertyType;
  alias?: string;
}) => {
  if (!party) return null;

  const propertyValue =
    party[propertyToServicePartyPropertyMap[property ?? fallbackValue]];

  return (
    <InlineTextWrapper property={property} href={propertyValue}>
      {alias ?? propertyValue}
    </InlineTextWrapper>
  );
};

const SaunaPrice = observer(() => {
  const t = localizationStore.getTranslation;

  const {
    saunas: [firstSauna],
  } = useOmaSATOSaunas();

  return (
    <InlineTextWrapper>
      {firstSauna?.price
        ? t('monthly_price_eur', { price: firstSauna.price })
        : t('sauna.monthly_price_approx')}
    </InlineTextWrapper>
  );
});

const AvailableParkingSpaces = observer(() => {
  const { data, isLoading } = useParkingProducts(
    apartmentStore.selectedApartment?.realestate,
    localizationStore.language
  );

  if (isLoading) {
    return <Skeleton className="inline-block h-4 w-6" />;
  }

  return (
    <InlineTextWrapper>{data?.rentableSpaces.length ?? 0}</InlineTextWrapper>
  );
});

export const InlineDynamicText = observer(
  ({
    context,
    property,
    alias,
  }: Pick<IInlineDynamicTextFields, 'context' | 'property' | 'alias'>) => {
    const { isLoading, laundryRoomReservationURL, ...properties } =
      useApartmentProperties();

    if (isLoading) return <Skeleton className="static h-4 w-full" />;

    switch (context) {
      case 'laundryRoomReservationURL':
        return (
          <InlineTextWrapper
            href={laundryRoomReservationURL}
            property="website"
          >
            {alias ?? laundryRoomReservationURL}
          </InlineTextWrapper>
        );
      case 'houseExpert':
      case 'serviceManager':
      case 'maintenanceCompany':
      case 'keyManagementCompany':
      case 'parkingSpaceKeyManagementCompany':
        return (
          <DynamicServicePartyProperty
            party={properties[context]}
            property={property}
          />
        );
      case 'saunaPrice':
        return <SaunaPrice />;
      case 'availableParkingSpaces':
        return <AvailableParkingSpaces />;
      case 'gsrnIdentifier':
        return apartmentStore.selectedApartment?.apartment.gsrnIdentifier;
      case 'internetProvider':
        return apartmentStore.selectedApartment?.apartment.internet?.provider;
      case 'safetyPlanLink': {
        const safetyPlanLink =
          apartmentStore.selectedApartment?.realestate.pelSu;

        if (!safetyPlanLink) return null;

        return (
          <InlineTextWrapper href={safetyPlanLink} property="website">
            {alias ?? safetyPlanLink}
          </InlineTextWrapper>
        );
      }
      default:
        return null;
    }
  }
);
